// Two Way Mapping — pair two buttons so they mirror each other, like a
// staircase switch. Ported from twoWayFunctions.dart.
//
// ── The app only manages rows ──────────────────────────────────────────────
// A pair is one row in the backend's TwoWayMapping table:
//
//     FirstUniqueNumber  / FristButtonNumber     <- the backend's typo
//     SecondUniqueNumber / SecondButtonNumber
//     MqttMessage, LastActionTime
//
// Nothing is mirrored here. When a mapped button is pressed the backend's
// ProcessTwoWayButtonAction looks up the partner and publishes to the partner's
// `<uno>/device` topic.
//
// ⚠️ THREE THINGS THAT LOOK LIKE DETAILS AND ARE NOT:
//
// 1. `FristButtonNumber` is spelled that way in the database. Spelling it
//    correctly writes a column that does not exist.
//
// 2. `LastActionTime` MUST NOT BE NULL. The backend's lookup only returns rows
//    where TIMESTAMPDIFF(SECOND, LastActionTime, NOW()) > 3 — the loop-breaker
//    that stops a mirrored press bouncing back and forth forever. With NULL that
//    expression is NULL, NULL > 3 is never true, and the mapping silently never
//    fires. So a new row is seeded with an old date rather than left empty.
//
// 3. A pair may be stored in EITHER direction, so both reading and deleting
//    have to match both ways round.

import { post } from '../api/api';

// The conventional "never happened" date, in the format the server writes.
export const NEVER = '1901-01-01 00:00:00';

// The delete `where` is built as SQL text by the backend, so a unique number
// that is not plain alphanumeric is refused rather than interpolated. Unique
// numbers are digits (older hardware) or uppercase hex (ESP8684) — never
// anything needing quoting.
const SAFE_UNO = /^[A-Za-z0-9]+$/;
export const isSafeUniqueNumber = (u) => SAFE_UNO.test(String(u ?? ''));

const int = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const str = (v) => String(v ?? '');

/**
 * Every pair involving this device, normalised so `partner` is always the OTHER
 * end — regardless of which side of the row this device sits on.
 */
export async function getMappingsFor(uniqueNumber) {
  const res = await post('GetTwoWayAllButtonsDetailsByUniqueNumber', {
    UniqueNumber: uniqueNumber,
  });
  const rows = Array.isArray(res?.data) ? res.data : [];
  const uno = str(uniqueNumber);

  return rows.map((r) => {
    const firstIsMine = str(r.FirstUniqueNumber) === uno;
    return {
      output: int(firstIsMine ? r.FristButtonNumber : r.SecondButtonNumber),
      partnerUniqueNumber: str(firstIsMine ? r.SecondUniqueNumber : r.FirstUniqueNumber),
      partnerOutput: int(firstIsMine ? r.SecondButtonNumber : r.FristButtonNumber),
      partnerDeviceName: str(r.TempDeviceName || r.DeviceName || ''),
      partnerOutputName: str(r.TempOutputName || r.OutputName || ''),
      raw: r,
    };
  });
}

/** The pairs on one button. */
export const mappingsForOutput = (mappings, output) =>
  (mappings || []).filter((m) => m.output === int(output));

/** Create a pair. Outputs are 1-based. */
export async function addTwoWayMapping({
  firstUniqueNumber, firstOutput, secondUniqueNumber, secondOutput,
}) {
  // The body IS the row — AddTwoWayMapping inserts it verbatim, so these are
  // column names, not a payload shape of our choosing.
  await post('AddTwoWayMapping', {
    FirstUniqueNumber: str(firstUniqueNumber),
    FristButtonNumber: int(firstOutput),
    SecondUniqueNumber: str(secondUniqueNumber),
    SecondButtonNumber: int(secondOutput),
    // Only read for IR-style devices; for switches the backend publishes the
    // partner's output number instead.
    MqttMessage: '',
    LastActionTime: NEVER,
  });
  return true;
}

/** Remove a pair, matching whichever direction it was stored in. */
export async function deleteTwoWayMapping(uniqueNumber, output, partnerUniqueNumber, partnerOutput) {
  const a = str(uniqueNumber);
  const p = str(partnerUniqueNumber);
  if (!isSafeUniqueNumber(a) || !isSafeUniqueNumber(p)) {
    throw new Error('Refusing to delete: unexpected unique number.');
  }
  const ao = int(output);
  const po = int(partnerOutput);
  const where = `(FirstUniqueNumber = '${a}' AND FristButtonNumber = ${ao}`
    + ` AND SecondUniqueNumber = '${p}' AND SecondButtonNumber = ${po})`
    + ` OR (FirstUniqueNumber = '${p}' AND FristButtonNumber = ${po}`
    + ` AND SecondUniqueNumber = '${a}' AND SecondButtonNumber = ${ao})`;
  await post('DeleteTwoWayMapping', { where });
  return true;
}
